/**
 * PATH: packages/ui/src/components/settings/OrganisationTab.tsx
 * FILE_ID: UI-SETTINGS-ORG-001
 * PURPOSE: Orchestrator for organisation settings — owns state, validation, and save logic
 * WHY: Display and edit organisation-level settings (admin only)
 * FLOW:
 * ┌──────────────────┐   ┌───────────────────┐   ┌──────────────┐
 * │ OrganisationData │ → │ OrganisationTab   │ → │ OrgFormFields│
 * │ + callbacks      │   │ (state + handlers)│   │ (presentation)│
 * └──────────────────┘   └───────────────────┘   └──────────────┘
 * DEPENDENCIES:
 *   - react: useState, useCallback for form state management
 *   - lucide-react: icons for logo fallback and action buttons
 *   - ../../lib/utils: cn() utility
 *   - ./organisation-tab/organisation-tab-types: shared type definitions
 *   - ./organisation-tab/OrgFormFields: presentational form fields
 */

import { useState, useCallback } from 'react'
import { Building2, Loader2, Check, X, ExternalLink } from 'lucide-react'
import { cn } from '../../lib/utils'
import { OrgFormFields } from './organisation-tab/OrgFormFields'
import { QuoteBuilderModeSelector } from './organisation-tab/QuoteBuilderModeSelector'
import type {
  OrganisationData,
  OrganisationTabProps,
  QuoteBuilderModeValue,
} from './organisation-tab/organisation-tab-types'

// Re-export types so existing consumers continue to work
export type { OrganisationData, OrganisationTabProps } from './organisation-tab/organisation-tab-types'

// =============================================================================
// HELPERS
// =============================================================================

/** Build a pristine form state from organisation data, defaulting empty strings */
function buildFormState(org: OrganisationData): OrganisationData {
  return {
    name: org.name || '',
    company_name: org.company_name || '',
    abn: org.abn || '',
    acn: org.acn || '',
    email: org.email || '',
    phone: org.phone || '',
    website: org.website || '',
    address: org.address || '',
    city: org.city || '',
    state: org.state || '',
    postcode: org.postcode || '',
    country: org.country || '',
    timezone: org.timezone || '',
    // Carry the Quote Builder trade mode through so the selector
    // round-trips correctly. Undefined when the upstream API hasn't
    // returned the field yet (older Organisation rows) — the selector
    // falls back to the backend default 'waterproofing' for display.
    quote_builder_mode: org.quote_builder_mode,
  }
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function OrganisationTab({
  organisation,
  isLoading = false,
  isSaving = false,
  onSave,
  onLogoChange,
  editable = true,
  className,
}: OrganisationTabProps) {
  const [formData, setFormData] = useState<OrganisationData>(() => buildFormState(organisation))
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleChange = useCallback((name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }))
    setHasChanges(true)
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }, [errors])

  const handleQuoteBuilderModeChange = useCallback((next: QuoteBuilderModeValue) => {
    setFormData(prev => ({ ...prev, quote_builder_mode: next }))
    setHasChanges(true)
  }, [])

  const handleCancel = useCallback(() => {
    setFormData(buildFormState(organisation))
    setHasChanges(false)
    setErrors({})
  }, [organisation])

  const handleSave = useCallback(async () => {
    if (!onSave) return

    const newErrors: Record<string, string> = {}
    if (!formData.name?.trim()) {
      newErrors.name = 'Organisation name is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await onSave(formData)
      setHasChanges(false)
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to save organisation:', err)
    }
  }, [formData, onSave])

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    )
  }

  const fieldsDisabled = !editable || isSaving

  return (
    <div className={cn('space-y-8', className)}>
      {/* Logo Section */}
      <div className="flex items-start gap-6 pb-6 border-b border-slate-200 dark:border-slate-800">
        <div className="relative group">
          {organisation.logo_url ? (
            <img
              src={organisation.logo_url}
              alt={organisation.name}
              className="w-20 h-20 rounded-xl object-contain bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2"
            />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          )}

          {editable && onLogoChange && (
            <label className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
              <span className="text-xs text-white font-medium">Change</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) onLogoChange(file)
                }}
                className="hidden"
              />
            </label>
          )}
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {organisation.company_name || organisation.name}
          </h3>
          {organisation.abn && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              ABN: {organisation.abn}
            </p>
          )}
          {organisation.website && (
            <a
              href={organisation.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-teal-600 dark:text-teal-400 hover:underline mt-2"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {organisation.website}
            </a>
          )}
        </div>
      </div>

      {/* Form Fields (presentational) */}
      <OrgFormFields
        formData={formData}
        errors={errors}
        onChange={handleChange}
        disabled={fieldsDisabled}
      />

      {/* Quote Builder trade mode (waterproofing / steel / custom). Lives
          below the address fields so an admin sets it once at onboarding
          time and can revisit it without scrolling past every input. */}
      <QuoteBuilderModeSelector
        value={formData.quote_builder_mode}
        onSelect={handleQuoteBuilderModeChange}
        disabled={fieldsDisabled}
      />

      {/* Actions */}
      {editable && onSave && hasChanges && (
        <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              'bg-teal-500 text-white hover:bg-teal-600',
              isSaving && 'opacity-60 cursor-not-allowed'
            )}
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Changes
          </button>

          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

export default OrganisationTab
